import { callOpenAI } from './openai';
import { callAnthropic } from './anthropic';
import { callGoogle } from './google';
import { callXAI } from './xai';
import { callMistral } from './mistral';
import { callGroq } from './groq';
import { callOpenRouter } from './openrouter';
import { callDeepSeek } from './deepseek';
import { resolveModelId } from '../data/models';

export { getKey, parseProviderError, ProviderError, testProvider, callOpenAICompat } from './shared';
export { callOpenAI } from './openai';
export { callAnthropic } from './anthropic';
export { callGoogle } from './google';
export { callXAI } from './xai';
export { callMistral } from './mistral';
export { callGroq } from './groq';
export { callOpenRouter } from './openrouter';
export { callDeepSeek } from './deepseek';

/* opts: { signal?, maxOutTokens?, attachments? }
 *   - maxOutTokens overrides the model's default cap (synthesis uses this
 *     to request the model's full ceiling).
 *   - attachments[] is the prompt-attachments queue (see modules/attachments.ts).
 *     The dispatcher routes each binary part to providers that support it
 *     (images → vision models, PDFs → Anthropic / Gemini) and replaces
 *     unsupported parts with short text notes so the run still completes.
 */
export async function callModel(modelDef, prompt, signalOrOpts, opts) {
  // Back-compat: callers used to pass `signal` as the 3rd arg. Accept either
  // a bare AbortSignal OR a full opts object.
  let resolvedOpts;
  if (signalOrOpts && typeof signalOrOpts === 'object' && 'aborted' in signalOrOpts) {
    resolvedOpts = { signal: signalOrOpts, ...(opts || {}) };
  } else {
    resolvedOpts = signalOrOpts || {};
  }
  // Each adapter accepts (modelId, prompt, signal, opts). Adapters fall back
  // to modelDef.maxOut when opts.maxOutTokens is undefined.
  const { signal } = resolvedOpts;

  // Triage the attachments per-model. Three independent buckets:
  //   - textChunks: text-shaped attachments (incl. extracted DOCX/PPTX text)
  //     prepended to the prompt as fenced blocks. Works for EVERY model.
  //   - keptParts:  binary parts the model can natively handle (image for
  //     vision models, pdf for PDF-capable models). Passed to the adapter
  //     to be rendered as provider-specific content blocks.
  //   - skipNotes:  short notes for anything the model can't ingest, so it
  //     at least knows the user tried to attach something.
  // Previous version dropped textChunks silently — the headline bug found
  // in the post-ship audit.
  let augmentedPrompt = prompt;
  const visionOk = !!modelDef.vision;
  const pdfOk    = !!modelDef.pdf;
  const allParts = Array.isArray(resolvedOpts.attachments) ? resolvedOpts.attachments : [];
  const keptParts = [];
  const skipNotes = [];
  const textChunks = [];
  const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  for (const a of allParts) {
    if (a.kind === 'text' && a.text) {
      const header = a.sourceMime === DOCX ? `--- attached Word file: ${a.name} ---`
                   : a.sourceMime === PPTX ? `--- attached PowerPoint file: ${a.name} ---`
                   : `--- attached file: ${a.name} ---`;
      textChunks.push(`${header}\n${a.text}\n--- end ${a.name} ---`);
    }
    else if (a.kind === 'image' && visionOk) keptParts.push(a);
    else if (a.kind === 'image' && !visionOk) skipNotes.push(`[image attached but ${modelDef.label} does not accept vision: ${a.name}]`);
    else if (a.kind === 'pdf'   && pdfOk)    keptParts.push(a);
    else if (a.kind === 'pdf'   && !pdfOk)   skipNotes.push(`[PDF attached but ${modelDef.label} does not accept PDFs natively: ${a.name}]`);
    else if (a.kind === 'binary') skipNotes.push(`[binary attached: ${a.name}]`);
  }
  if (textChunks.length) augmentedPrompt = augmentedPrompt + '\n\n' + textChunks.join('\n\n');
  if (skipNotes.length)  augmentedPrompt = augmentedPrompt + '\n\n' + skipNotes.join('\n');

  const callOpts = {
    maxOutTokens: resolvedOpts.maxOutTokens ?? modelDef.maxOut,
    parts: keptParts,
  };
  // Route deprecated ids to their current successor at API-call time. The
  // catalog (`MODELS`) keeps the legacy row so labels + pricing still
  // resolve on the response side, but the wire request goes to the new
  // model name. Silent + safe — see MODEL_ID_ALIASES in data/models.ts.
  const wireId = resolveModelId(modelDef.id);
  switch (modelDef.provider) {
    case "openai":     return callOpenAI(wireId, augmentedPrompt, signal, callOpts);
    case "anthropic":  return callAnthropic(wireId, augmentedPrompt, signal, callOpts);
    case "google":     return callGoogle(wireId, augmentedPrompt, signal, callOpts);
    case "xai":        return callXAI(wireId, augmentedPrompt, signal, callOpts);
    case "mistral":    return callMistral(wireId, augmentedPrompt, signal, callOpts);
    case "groq":       return callGroq(wireId, augmentedPrompt, signal, callOpts);
    case "openrouter": return callOpenRouter(wireId, augmentedPrompt, signal, callOpts);
    case "deepseek":   return callDeepSeek(wireId, augmentedPrompt, signal, callOpts);
    default: throw new Error(`Unknown provider: ${modelDef.provider}`);
  }
}

/* Generic dispatcher convenience: callProvider(modelId, prompt, key?, opts?) */
export async function callProvider(modelId, prompt, _key, opts) {
  const { MODELS } = await import('../data/models');
  const m = MODELS.find(x => x.id === modelId);
  if (!m) throw new Error(`Unknown model: ${modelId}`);
  return callModel(m, prompt, opts || {});
}
